// Pure sort + filter helpers for EntityTable. Kept free of React so the
// ordering/matching contract can be unit-tested directly and reused by the
// localStorage-backed useTablePreferences hook.
// Resolve a column's comparable value on a row: an explicit sortAccessor wins,
// otherwise fall back to the primitive value at `key`. Nullish becomes '' so a
// row with a missing field sorts/filters as empty rather than throwing.
function comparableValue(row, col) {
    if (col.sortAccessor) return col.sortAccessor(row);
    const raw = row[col.key];
    if (typeof raw === 'number') return raw;
    return raw == null ? '' : String(raw);
}
// Stable-ish sort by one column. Numbers compare numerically; everything else
// uses a natural, case-insensitive locale compare so "item 2" precedes
// "item 10" and "rfi" beside "RFI". Returns the original array reference when
// there is nothing to sort, so callers pay no allocation cost on the no-op path.
export function sortRows(rows, columns, sort) {
    if (!sort) return rows;
    const col = columns.find((c)=>c.key === sort.key);
    if (!col) return rows;
    return [
        ...rows
    ].sort((a, b)=>{
        const av = comparableValue(a, col);
        const bv = comparableValue(b, col);
        const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv), undefined, {
            numeric: true,
            sensitivity: 'base'
        });
        return sort.dir === 'asc' ? cmp : -cmp;
    });
}
// Apply every active filter (AND across columns). 'select' filters match
// exactly (case-insensitive); 'text' filters match as a case-insensitive
// substring. Blank/absent filter values are ignored, so an empty filter map is
// a no-op that returns the original array reference.
export function filterRows(rows, columns, filters) {
    const active = Object.entries(filters).filter(([, v])=>v != null && v !== '');
    if (active.length === 0) return rows;
    return rows.filter((row)=>active.every(([key, want])=>{
            const col = columns.find((c)=>c.key === key);
            if (!col) return true;
            const raw = col.sortAccessor ? col.sortAccessor(row) : row[col.key];
            const hay = (raw == null ? '' : String(raw)).toLowerCase();
            const needle = want.toLowerCase();
            return col.filter === 'select' ? hay === needle : hay.includes(needle);
        }));
}
// Filter first (cheaper, shrinks the set), then sort. Convenience used by
// EntityTable; composed from the two pure helpers above.
export function applyTableView(rows, columns, sort, filters) {
    return sortRows(filterRows(rows, columns, filters), columns, sort);
}
